import { motion } from "framer-motion";
import { Mail, Clock, Timer } from "lucide-react";
import Logo from "@/components/Logo";

const easeOut = [0.19, 1, 0.22, 1];

export default function Contact() {
  return (
    <div className="min-h-[calc(100vh-80px)] bg-background text-foreground" data-testid="contact-page">
      <section className="max-w-4xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-16">
        {/* Header */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: easeOut }}
          className="label-xs opacity-60"
        >
          Reach Us
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOut, delay: 0.05 }}
          className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-serif tracking-tighter leading-[0.95]"
        >
          Get in Touch.
        </motion.h1>

        {/* Premium message */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: easeOut, delay: 0.2 }}
          className="mt-10 max-w-xl"
          data-testid="contact-message"
        >
          <p className="font-serif italic text-2xl md:text-3xl leading-snug tracking-tight">
            We're here to help.
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed opacity-80">
            For product inquiries, collaborations, or support, feel free to email us.
            We'll get back to you as soon as possible.
          </p>
        </motion.div>
      </section>

      {/* Details grid */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 pb-24">
        <div className="border-t border-b divide-y md:divide-y-0 md:divide-x md:grid md:grid-cols-3">
          {/* Email */}
          <motion.a
            href="mailto:thefabrics37@gmail.com"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="group block p-8 md:p-10 transition-colors"
            style={{ transitionProperty: "background-color" }}
            data-testid="contact-email-block"
          >
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 opacity-70" strokeWidth={1.5} />
              <span className="label-xs opacity-60">Email</span>
            </div>
            <p className="mt-6 font-serif text-2xl md:text-3xl tracking-tight leading-tight break-all group-hover:underline underline-offset-4">
              thefabrics37@gmail.com
            </p>
            <p className="mt-2 text-xs opacity-50 tracking-wider">Tap to compose</p>
          </motion.a>

          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.1 }}
            className="p-8 md:p-10"
            data-testid="contact-hours-block"
          >
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 opacity-70" strokeWidth={1.5} />
              <span className="label-xs opacity-60">Business Hours</span>
            </div>
            <p className="mt-6 font-serif text-2xl md:text-3xl tracking-tight leading-snug">
              Monday — Friday
            </p>
            <p className="mt-2 text-base opacity-80">10:00 AM – 7:00 PM <span className="opacity-60">(IST)</span></p>
          </motion.div>

          {/* Response time */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: easeOut, delay: 0.2 }}
            className="p-8 md:p-10"
            data-testid="contact-response-block"
          >
            <div className="flex items-center gap-3">
              <Timer className="h-4 w-4 opacity-70" strokeWidth={1.5} />
              <span className="label-xs opacity-60">Response Time</span>
            </div>
            <p className="mt-6 font-serif text-2xl md:text-3xl tracking-tight leading-snug">
              Usually within
            </p>
            <p className="mt-2 text-2xl md:text-3xl font-serif tracking-tight">24 hours.</p>
          </motion.div>
        </div>
      </section>

      {/* Sign-off with logo */}
      <section className="pb-24 pt-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: easeOut }}
          className="flex flex-col items-center text-center"
          data-testid="contact-signoff"
        >
          <Logo variant="monogram" className="h-14 w-auto dark:invert" />
          <div className="mt-6 font-display tracking-[0.4em] text-sm md:text-base">THE FABRICS</div>
          <div className="mt-2 text-[10px] md:text-[11px] tracking-[0.5em] opacity-60">
            WEAR YOUR IDENTITY.
          </div>
        </motion.div>
      </section>
    </div>
  );
}
